import { Body, Box, Button, HStack, Heading, Sheet } from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React, { useMemo, useState } from 'react';

import { Masonry } from 'src/modules/homeownerApp/components/Masonry';
import { useCreateAiDisplayImageAndUpdateCache } from 'src/modules/homeownerApp/components/Utils';
import { FAVORITES_PAGE_SIZE } from 'src/modules/homeownerApp/constants';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { useAiDisplayImagesQuery } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { logError } from 'src/shared/telemetry/logger';

import { useCurrentProject } from '../EditProject';

import { SavedItem } from './SavedItem';

const AddSavedToProject = () => {
  const { setCurrentMenu } = useHomeownerAppContext();
  const [selectedImages, setSelectedImages] = useState<AiDisplayImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { project } = useCurrentProject();
  const createAiDisplayImage = useCreateAiDisplayImageAndUpdateCache();

  const { data: displayImagesData, loading: isLoadingFavoritedDisplayImages } =
    useAiDisplayImagesQuery({
      variables: { favorite: true, perPage: FAVORITES_PAGE_SIZE },
    });

  const displayImages = useMemo(() => {
    const existingGeneratedImageIds = project?.addables?.map(
      ({ aiGeneratedImage }) => aiGeneratedImage.id,
    );
    const favoritedDisplayImages =
      displayImagesData?.aiDisplayImages?.results.filter(displayImage => {
        const relevantGeneratedImageId = displayImage?.aiGeneratedImage
          ?.id as number;
        return !existingGeneratedImageIds?.includes(relevantGeneratedImageId);
      }) || [];
    return favoritedDisplayImages;
  }, [project, displayImagesData]);

  const handleImageClick = (displayImage: AiDisplayImage | null) => {
    if (!displayImage) return;
    if (selectedImages.includes(displayImage)) {
      setSelectedImages(
        selectedImages.filter(
          selectedImage => selectedImage.id !== displayImage.id,
        ),
      );
    } else {
      setSelectedImages([...selectedImages, displayImage]);
    }
  };

  const handleSubmitClick = async () => {
    if (!project) return;
    setIsLoading(true);
    try {
      await Promise.all(
        selectedImages.map(displayImage => {
          const generatedImage = displayImage.aiGeneratedImage;
          if (!generatedImage) return Promise.resolve();
          return createAiDisplayImage(generatedImage, project.id);
        }),
      );
      setCurrentMenu(null);
    } catch (error) {
      logError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Sheet.Contents
      header={
        <HStack
          justifyContent={{ base: 'center', tablet: 'space-between' }}
          width="100%"
        >
          <Body
            display={{ base: 'flex', tablet: 'none' }}
            isSemiBold
            marginY="0px"
          >
            {t('projects.addIdeas.selectFromSaved')}
          </Body>
          <Heading display={{ base: 'none', tablet: 'flex' }} size={700}>
            {t('projects.addIdeas.selectFromSaved')}
          </Heading>
          <Button
            display={{ base: 'none', tablet: 'flex' }}
            isDisabled={
              selectedImages.length === 0 || isLoadingFavoritedDisplayImages
            }
            isLoading={isLoading}
            onClick={handleSubmitClick}
          >
            {t('projects.addIdeas.addToProject')}
          </Button>
        </HStack>
      }
      maxContentHeight={{ base: 'calc(100dvh - 75px)', desktop: '70vh' }}
    >
      <Masonry
        columns={{ base: 2, tablet: 3 }}
        fallbackRowHeight={{ base: 250, width250: 300, tablet: 400 }}
        gap={{ base: 300, tablet: 500 }}
        isLoading={isLoadingFavoritedDisplayImages}
        marginTop={300}
        role="list"
        width="100%"
      >
        <AnimatePresence>
          {displayImages.map(displayImage => {
            if (!displayImage) return null;
            const isSelected =
              !!displayImage && selectedImages?.includes(displayImage);
            return (
              <SavedItem
                displayImage={displayImage}
                isSelected={isSelected}
                key={displayImage.id}
                onClick={() => handleImageClick(displayImage)}
              />
            );
          })}
        </AnimatePresence>
      </Masonry>
      <Box
        backgroundColor="neutral.0"
        bottom="var(--motion-overflow)"
        display={{ base: 'block', tablet: 'none' }}
        left={0}
        paddingX={500}
        paddingY={300}
        position="absolute"
        right={0}
        width="100%"
      >
        <Button
          isDisabled={
            selectedImages.length === 0 || isLoadingFavoritedDisplayImages
          }
          isLoading={isLoading}
          onClick={handleSubmitClick}
          size="large"
          width="100%"
        >
          {t('projects.addIdeas.addToProject')}
        </Button>
      </Box>
    </Sheet.Contents>
  );
};

export { AddSavedToProject };
